import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { signIn, signOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import FleetOverview from './components/FleetOverview';
import CallsAlerts from './components/CallsAlerts';
import DocumentsPage from './components/DocumentsPage';
import MotorDetail from './components/MotorDetail';
import LoginPage from './components/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './components/HomePage';
import { PrivacyPage, TermsPage } from './components/LegalPage';
import PortalShell from './components/PortalShell';
import ScreeningApp from './components/ScreeningApp';
import awsConfig from './aws-exports';

// Dev-only screenshot harness. process.env.NODE_ENV is a build-time constant,
// so in a production build this is null and the module is dropped entirely.
const DevPortalPreview = process.env.NODE_ENV !== 'production'
  ? require('./DevPortalPreview').default
  : null;

Amplify.configure(awsConfig);

const SHOP_PHONE = '(423) 790-4745';

// ── Theme hook ─────────────────────────────────────────────────────────────

function useTheme() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('byrdsense-theme') || 'light'; } catch { return 'light'; }
  });

  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
    try { localStorage.setItem('byrdsense-theme', theme); } catch { /* ignore */ }
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  return { theme, toggleTheme };
}

// ── Theme colors helper ────────────────────────────────────────────────────

// ── Auth context shared across routes ──────────────────────────────────────

function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState({ customerId: null, customerName: null, email: null });
  const [loginError, setLoginError] = useState('');

  const checkAuth = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      if (session?.tokens) {
        const attrs = await fetchUserAttributes();
        setCustomerInfo({
          customerId: attrs['custom:customerId'] || null,
          customerName: attrs['custom:customerName'] || null,
          email: attrs.email || null,
        });
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        if (!session?.tokens) setIsAuthenticated(false);
      } catch { console.warn('Session refresh failed'); }
    }, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLogin = async (email, password) => {
    setLoginError('');
    try {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        await checkAuth();
        return { success: true };
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setLoginError('FORCE_NEW_PASSWORD');
        return result;
      }
    } catch (err) {
      setLoginError(err.message || 'Login failed');
    }
    return null;
  };

  const handleSignOut = async () => {
    try { await signOut(); } catch { /* non-fatal */ }
    setIsAuthenticated(false);
    setCustomerInfo({ customerId: null, customerName: null, email: null });
  };

  return { isAuthenticated, isLoading, customerInfo, loginError, setLoginError, handleLogin, handleSignOut };
}

// ── Protected route wrapper ────────────────────────────────────────────────

function ProtectedRoute({ auth, children }) {
  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A1B3B' }}>
        <div className="text-center">
          <img src="/assets/brand/byrdsense-wordmark-reversed.svg" alt="Byrdsense" className="w-40 mx-auto mb-6" />
          <div className="w-10 h-10 border-4 border-navy/20 border-t-navy rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 font-medium text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// ── Portal layout (header + footer wrapping portal pages) ──────────────────

function PortalLayout({ auth, themeCtx, children }) {
  // Navy sidebar + ivory main, per design/mockups/portal.mockup.html. The theme
  // toggle and sign-out moved into the shell; routes and auth are unchanged.
  return (
    <PortalShell
      customerId={auth.customerInfo.customerId}
      customerName={auth.customerInfo.customerName}
      email={auth.customerInfo.email}
      onSignOut={auth.handleSignOut}
      theme={themeCtx.theme}
      onToggleTheme={themeCtx.toggleTheme}
    >
      {children}
    </PortalShell>
  );
}

// ── Fleet page ─────────────────────────────────────────────────────────────

function FleetPage({ auth, themeCtx }) {
  const navigate = useNavigate();
  return (
    <PortalLayout auth={auth} themeCtx={themeCtx}>
      <FleetOverview
        customerId={auth.customerInfo.customerId}
        customerName={auth.customerInfo.customerName}
        onSelectMotor={(motor) => navigate(`/motor/${motor.motorId || motor.id}`)}
        shopPhone={SHOP_PHONE}
      />
    </PortalLayout>
  );
}

// ── Calls & alerts page ────────────────────────────────────────────────────

function CallsPage({ auth, themeCtx }) {
  const navigate = useNavigate();
  return (
    <PortalLayout auth={auth} themeCtx={themeCtx}>
      <CallsAlerts
        customerId={auth.customerInfo.customerId}
        customerName={auth.customerInfo.customerName}
        onSelectMotor={(motorId) => navigate(`/motor/${motorId}`)}
        shopPhone={SHOP_PHONE}
      />
    </PortalLayout>
  );
}

// ── Reports / Screenings pages ─────────────────────────────────────────────

function DocsPage({ auth, themeCtx, kind }) {
  return (
    <PortalLayout auth={auth} themeCtx={themeCtx}>
      <DocumentsPage
        kind={kind}
        customerId={auth.customerInfo.customerId}
        customerName={auth.customerInfo.customerName}
        shopPhone={SHOP_PHONE}
      />
    </PortalLayout>
  );
}

// ── Motor detail page ──────────────────────────────────────────────────────

function MotorPage({ auth, themeCtx }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [motor, setMotor] = useState(null);

  useEffect(() => {
    const { customerId } = auth.customerInfo;
    if (!customerId) return;
    import('./services/monitoringApi').then(({ listMotors }) => {
      listMotors(customerId).then(result => {
        const motors = result.data || result.motors || [];
        const found = motors.find(m => (m.motorId || m.id) === id);
        if (found) setMotor(found);
      }).catch(() => {});
    });
  }, [id, auth.customerInfo]);

  if (!motor) {
    return (
      <PortalLayout auth={auth} themeCtx={themeCtx}>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="w-10 h-10 border-4 border-navy/20 border-t-navy rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading asset...</p>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout auth={auth} themeCtx={themeCtx}>
      <MotorDetail
        motor={motor}
        onBack={() => navigate('/fleet')}
        shopPhone={SHOP_PHONE}
      />
    </PortalLayout>
  );
}

// ── Login page with redirect on success ────────────────────────────────────

function LoginRoute({ auth }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      navigate('/fleet', { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  const handleLogin = async (email, password) => {
    const result = await auth.handleLogin(email, password);
    if (result?.success) {
      navigate('/fleet', { replace: true });
    }
    return result;
  };

  return <LoginPage onLogin={handleLogin} error={auth.loginError} setError={auth.setLoginError} />;
}

// ── App root ───────────────────────────────────────────────────────────────

function AppContent() {
  const auth = useAuth();
  const themeCtx = useTheme();

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/screening" element={<ScreeningApp />} />
      {/* Dev-only restyle preview; harmless in prod but not linked anywhere. */}
      {DevPortalPreview && <Route path="/__preview" element={<DevPortalPreview />} />}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/login" element={<LoginRoute auth={auth} />} />

      <Route path="/dashboard" element={
        <ProtectedRoute auth={auth}><Navigate to="/fleet" replace /></ProtectedRoute>
      } />
      <Route path="/fleet" element={
        <ProtectedRoute auth={auth}><FleetPage auth={auth} themeCtx={themeCtx} /></ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute auth={auth}><DocsPage auth={auth} themeCtx={themeCtx} kind="reports" /></ProtectedRoute>
      } />
      <Route path="/screenings" element={
        <ProtectedRoute auth={auth}><DocsPage auth={auth} themeCtx={themeCtx} kind="screenings" /></ProtectedRoute>
      } />
      <Route path="/calls" element={
        <ProtectedRoute auth={auth}><CallsPage auth={auth} themeCtx={themeCtx} /></ProtectedRoute>
      } />
      <Route path="/motor/:id" element={
        <ProtectedRoute auth={auth}><MotorPage auth={auth} themeCtx={themeCtx} /></ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
